import type { ComputedRef, Ref } from 'vue'
import type { ReceiptModel, ReceiptModelEnum } from '~/utils/receipt-model/ReceiptModel'

export interface UseReceiptModelOptionsResult {
  payoutOptions: ComputedRef<string[]>
  selectedPayoutOption: Ref<string>
  selectPayoutOption: (code: string) => void
  receiptModel: ComputedRef<ReceiptModelEnum | ''>
}

export function useReceiptModelOptions(
  paymentModelData: Ref<ReceiptModel | undefined>,
): UseReceiptModelOptionsResult {
  const selectedPayoutOption = ref<string>('')

  const payoutOptions = computed<string[]>(() => {
    const data = paymentModelData.value
    if (data?.payout_options?.length)
      return data.payout_options
    return data?.payout_option ? [data.payout_option] : []
  })

  const receiptModel = computed<ReceiptModelEnum | ''>(() => paymentModelData.value?.receipt_model ?? '')

  function getInitialPayoutOption(): string {
    const fromBackend = paymentModelData.value?.payout_option
    if (fromBackend && payoutOptions.value.includes(fromBackend))
      return fromBackend
    return payoutOptions.value[0] ?? ''
  }

  function selectPayoutOption(code: string) {
    if (!payoutOptions.value.includes(code))
      return
    selectedPayoutOption.value = code
  }

  let isInitialized = false
  let lastBackendPayoutOption: string | undefined

  watch(paymentModelData, (data) => {
    if (!data)
      return

    const backendPayoutOption = data.payout_option
    const backendChanged = isInitialized && backendPayoutOption !== lastBackendPayoutOption
    const currentIsMissing = !payoutOptions.value.includes(selectedPayoutOption.value)

    if (!isInitialized || backendChanged || currentIsMissing)
      selectedPayoutOption.value = getInitialPayoutOption()

    isInitialized = true
    lastBackendPayoutOption = backendPayoutOption
  }, { immediate: true })

  return {
    payoutOptions,
    selectedPayoutOption,
    selectPayoutOption,
    receiptModel,
  }
}
